import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  Renderer2,
  Signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { combineLatest } from 'rxjs';

import type { RatingScale } from '@core/candidate-evaluation-settings-types';
import { EvaluationRatingScoreComponent } from '@core/evaluations-ui';
import { getRatingThumb } from '@core/evaluations-util';
import { InterviewEvaluationRatingComponent } from '@core/interview-results-ui';
import { getRatingName } from '@core/interview-results-util';
import { IconComponent } from '@core/user-interface-legacy';

@Component({
  selector: 'rt-evaluation-meter',
  templateUrl: './evaluation-meter.component.html',
  styleUrls: ['./evaluation-meter.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'state()',
    '[class.clickable]': 'isClickable()',
  },
  imports: [IconComponent, InterviewEvaluationRatingComponent, EvaluationRatingScoreComponent],
})
export class EvaluationMeterComponent {
  private readonly renderer = inject(Renderer2);

  public rating = input.required<number | null>();

  public isDisqualified = input<boolean, boolean | string>(false, { transform: booleanAttribute });

  public isHidden = input<boolean, boolean | string>(false, { transform: booleanAttribute });

  public isClickable = input<boolean, boolean | string>(true, { transform: booleanAttribute });

  public evaluationScale = input<RatingScale | null>(null);

  public hasEvaluationImprovements = input<boolean>(false);

  protected state = computed(() => {
    if (this.isDisqualified()) {
      return 'candidate-disqualified';
    } else if (this.isHidden()) {
      return 'rating-hidden';
    } else if (this.rating() === null) {
      return 'default';
    }

    const rating = this.rating()!;
    const scale = this.evaluationScale();

    if (!this.hasEvaluationImprovements() || !scale) {
      return getRatingName(rating);
    }

    return getRatingThumb(rating, scale.presentation).level;
  });

  private pathGroup = viewChild.required<ElementRef>('pathGroup');

  private paths: Signal<SVGPathElement[]> = computed(
    () => <SVGPathElement[]>Array.from(this.pathGroup().nativeElement.children).reverse(),
  );

  private onColorChanges = combineLatest([
    toObservable(this.isHidden),
    toObservable(this.isDisqualified),
    toObservable(this.rating),
  ])
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      this.markToColorFromFirstToPrc(this.rating());
    });

  private markToColorFromFirstToPrc(prcOfPathsToColor: number | null): void {
    /**
     * Ex.: ceil(33 / 100 * 25) = ceil(0.33 * 25) = ceil(8.25) = 9.
     * But, we always want to color at least one path, hence Math.max.
     * We subtract 1, because the index is zero-based.
     * We don't worry about values < 0 and values > 100.
     * For prcOfPathsToColor = null, we don't color any path.
     */
    const lastColoredPathIndex =
      prcOfPathsToColor === null
        ? -1
        : Math.max(1, Math.ceil((prcOfPathsToColor / 100) * this.paths().length)) - 1;

    this.paths().forEach((path, index) => {
      if (index <= lastColoredPathIndex) {
        this.renderer.addClass(path, 'colored');
      } else {
        this.renderer.removeClass(path, 'colored');
      }
    });
  }
}
